import { FunctionComponent, useEffect } from 'react';
import * as vp from 'vopay';
import type { FileUploadForm } from '../types';
import { useClientData } from '../../../../providers/ClientData.provider';
import { useForm } from '@/components/Form/utils';
import { useFileUploadDetailsFields, useFileUploadFields } from './buildFileUploadRows.helper';
import { Button, Title } from 'UI';
import { useTranslation } from 'react-i18next';
import { Box } from 'vopay';
const { Card } = vp;

type FileUploadFormProps = {
  upload: (values: FileUploadForm) => void;
  onValidate: (isValid: boolean) => void;
  defaultValues: FileUploadForm;
};

type FC = FunctionComponent<FileUploadFormProps>;

const FileUploadForm: FC = (props: FileUploadFormProps) => {
  const { upload, defaultValues } = props;
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.Form' });

  const downloadTemplate = (values: FileUploadForm) => {
    let fileName = '';

    switch (values.Product) {
      case 'EFT':
        fileName = 'eft.csv';
        break;
      case 'VopayInstant':
        fileName = 'VoPayInstant.csv';
        break;
      case 'e-Transfer':
        if (values.Service === 'Debit') {
          fileName = 'emt-debit.csv';
        } else {
          fileName = 'emt.csv';
        }

        break;
      case 'ePay':
        fileName = 'eLinx-pay.csv';
        break;
      case 'eCollect':
        fileName = 'eLinx-collect.csv';
        break;
      case 'eFlex':
        fileName = 'eLinx-flexible-payments.csv';
        break;
      case 'Credit Card':
        fileName = 'credit-card.csv';
        break;
      case 'OCT':
        fileName = 'oct.csv';
        break;
      case 'ACH':
        fileName = 'ach.csv';
        break;
      case 'RTP':
        fileName = 'rtp.csv';
        break;
      case 'Wire':
        fileName = 'wire.csv';
        break;
      case 'Client Account Transfer':
        fileName = 'client-account-transfer.csv';
        break;
      default:
        fileName = 'eft.csv';
        break;
    }

    void fetch(`/files/${fileName}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/csv',
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}`);

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode?.removeChild(link);
      });
  };

  const {
    response: { Country, clientAccountsEnabled },
  } = useClientData();

  const fileUploadDetailsConfig = useFileUploadDetailsFields(Country, clientAccountsEnabled);
  const fileUploadConfig = useFileUploadFields();
  const config = [...fileUploadDetailsConfig, ...fileUploadConfig];

  const { renderFormElements, ctx } = useForm<FileUploadForm>({
    initialValues: defaultValues,
    config,
    onSubmit: (values, ctx) => {
      upload({
        ...values,
        FileName: ctx.values.FileName,
      });
    },
  });

  useEffect(() => {
    // Set Product to first valid option when Service changes
    if (ctx.values.Service) {
      const getValidProductsForService = () => {
        if (ctx.values.Service === 'Credit') {
          // Credit products
          if (Country === 'US') {
            return ['ACH', 'ePay', 'OCT', 'RTP', 'Wire', ...(clientAccountsEnabled ? ['Client Account Transfer'] : [])];
          } else {
            return [
              'EFT',
              'VopayInstant',
              'e-Transfer',
              'ePay',
              'OCT',
              ...(clientAccountsEnabled ? ['Client Account Transfer'] : []),
            ];
          }
        } else if (ctx.values.Service === 'Debit') {
          // Debit products
          if (Country === 'US') {
            return ['ACH', 'Credit Card', 'eCollect', 'eFlex'];
          } else {
            return ['EFT', 'VopayInstant', 'e-Transfer', 'Credit Card', 'eCollect', 'eFlex'];
          }
        }

        return [];
      };

      const validProducts = getValidProductsForService();
      const currentProductIsValid = validProducts.includes(ctx.values.Product);

      if (!currentProductIsValid && validProducts.length > 0) {
        ctx.setFieldValue('Product', validProducts[0]);
      }
    }
  }, [ctx.values.Service, Country, clientAccountsEnabled]);

  useEffect(() => {
    props.onValidate(ctx.isValid);
  }, [ctx]);

  return (
    <form onSubmit={ctx.handleSubmit} id="fileUploadForm">
      <Card>
        <Box flx="col" gap="ml" base="8">
          <Title level="5">{t('cardTitle')}</Title>
          {renderFormElements({ config: fileUploadDetailsConfig })}

          <Box jc="fe">
            <Button
              id="submit-button"
              type="button"
              color="secondary"
              size="smallest"
              iconLeft="download"
              onClick={() => downloadTemplate(ctx.values)}
            >
              {t('downloadTemplateButton')}
            </Button>
          </Box>

          {renderFormElements({ config: fileUploadConfig })}
        </Box>
      </Card>
    </form>
  );
};

export default FileUploadForm;
